import { useCallback, useMemo } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router'
import {
  ROUTE_EXTERNAL_BANK,
  ROUTE_SUPPLIERS,
  PERMISSION_ENTITY_TREASURY,
  PERMISSION_ACTION_EDIT,
  ROUTE_KLEAR_ACCOUNT,
  ROUTE_MAKE_PAYMENT,
  buildNavPathTrader
} from '@klear/constants'
import { useAuth } from '@klear/ui/contexts/auth-context'
import { useTrader } from '@klear/ui/hooks/use-trader'
import {
  useSupplierExternalBankAccounts,
  useSupplierExternalBankAccountMutations
} from '@klear/ui/hooks/use-supplier-external-bank-accounts'
import { useSupplier } from '@klear/ui/hooks/use-suppliers'
import { useDocumentTitle } from '@klear/ui/hooks/use-document-title'
import toInteger from 'lodash/toInteger'
import {
  externalBankAccountFormToCreateWire,
  externalBankAccountFormToUpdateWire
} from '@klear/bank-account/wire.client'
import { EntityWizardShell } from '@klear/ui/wizard/entity-shell'
import { returnUrlFromSearchParams, useReturnUrlExit } from '@klear/ui/wizard/return-url'
import {
  ExternalBankAccountFlow,
  type ExternalBankAccountCommitArgs,
  type ExternalBankAccountFlowMode
} from '@/app/external-bank-account/shared/external-bank-account-step-group'

export interface SupplierExternalBankAccountWizardPageProps {
  mode: ExternalBankAccountFlowMode
}

export default function SupplierExternalBankAccountWizardPage({ mode }: SupplierExternalBankAccountWizardPageProps) {
  const { traderId, supplierId, externalBankAccountId } = useParams<{
    traderId?: string
    supplierId?: string
    externalBankAccountId?: string
  }>()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { user } = useAuth()
  const { urlPrefixApp } = useTrader(traderId)
  const supplierIdNum = supplierId ? toInteger(supplierId) : undefined
  const traderIdNum = traderId ? toInteger(traderId) : undefined
  const { supplier } = useSupplier(supplierIdNum ?? 0, traderIdNum)
  const accountIdNum = externalBankAccountId ? toInteger(externalBankAccountId) : null
  const canEdit = !!user?.hasPermission(PERMISSION_ENTITY_TREASURY, PERMISSION_ACTION_EDIT)

  const baseList = supplierIdNum
    ? `${urlPrefixApp}/${ROUTE_SUPPLIERS}/${supplierIdNum}`
    : `${urlPrefixApp}/${ROUTE_SUPPLIERS}`
  const baseWorkflow = supplierIdNum
    ? `${urlPrefixApp}/${ROUTE_SUPPLIERS}/${supplierIdNum}/${ROUTE_EXTERNAL_BANK}`
    : `${urlPrefixApp}/${ROUTE_SUPPLIERS}`

  const { supplierExternalBankAccounts, isLoading } = useSupplierExternalBankAccounts(supplierIdNum)
  const { commitSupplierExternalBankAccount, isCommitting } = useSupplierExternalBankAccountMutations()
  const account = useMemo(() => {
    if (mode === 'create' || accountIdNum == null) return null
    return supplierExternalBankAccounts.find((a) => a.id === accountIdNum) ?? null
  }, [supplierExternalBankAccounts, mode, accountIdNum])
  useDocumentTitle(
    account?.nameAndLast4,
    mode === 'create' ? 'New Bank Account' : mode === 'edit' ? 'Edit Bank Account' : 'Bank Account',
    supplier?.name
  )

  const { exit, exitAfterSave } = useReturnUrlExit(baseList)
  const goToEdit = useCallback(() => {
    if (accountIdNum != null) navigate(`${baseWorkflow}/${accountIdNum}/edit`)
  }, [navigate, baseWorkflow, accountIdNum])

  const navigateAfterSave = useCallback(() => {
    if (returnUrlFromSearchParams(searchParams) != null) {
      exitAfterSave()
      return
    }
    if (searchParams.get('returnToPayment') === 'true' && traderId) {
      const network = searchParams.get('network')
      const qs = new URLSearchParams()
      if (supplierIdNum) qs.set('supplierId', `${supplierIdNum}`)
      if (network) qs.set('network', network)
      navigate(
        `${buildNavPathTrader(traderId, ROUTE_KLEAR_ACCOUNT, ROUTE_MAKE_PAYMENT)}${qs.toString() ? `?${qs.toString()}` : ''}`
      )
      return
    }
    exitAfterSave()
  }, [searchParams, traderId, supplierIdNum, navigate, exitAfterSave])

  const onCommit = useCallback(
    async (args: ExternalBankAccountCommitArgs) => {
      if (supplierIdNum == null) throw new Error('Missing supplier context')
      if (args.mode === 'create') {
        return commitSupplierExternalBankAccount({
          supplierId: supplierIdNum,
          data: externalBankAccountFormToCreateWire(args.formValues)
        })
      }
      if (args.accountId == null) throw new Error('Missing bank account id')
      await commitSupplierExternalBankAccount({
        id: args.accountId,
        supplierId: supplierIdNum,
        data: externalBankAccountFormToUpdateWire(args.formValues, args.dirtyFields)
      })
    },
    [commitSupplierExternalBankAccount, supplierIdNum]
  )

  if (supplierIdNum == null) {
    return <div className="container mx-auto px-4 py-8 text-center">Select a supplier first</div>
  }

  const title = mode === 'create' ? 'Add bank account' : (account?.nameAndLast4 ?? 'Bank account')

  return (
    <div className="h-[calc(100vh-4rem)]">
      <EntityWizardShell
        mode={mode}
        entityLabel="bank account"
        title={title}
        canEdit={canEdit}
        isLoading={mode !== 'create' && isLoading}
        notFound={mode !== 'create' && !account}
        notFoundLabel="Bank account not found"
        onBack={exit}
        onEdit={goToEdit}
        isBusy={isCommitting}
      >
        {({ activeMode, onNavStateChange, cancelEdit, requestExit, onDirtyChange }) => (
          <ExternalBankAccountFlow
            key={`${activeMode}:${accountIdNum ?? 'new'}`}
            mode={activeMode}
            accountId={accountIdNum ?? undefined}
            account={account}
            addressSeed={supplier?.address ?? null}
            isCommitting={isCommitting}
            cancelOnFirstStep={activeMode === 'create'}
            onNavStateChange={onNavStateChange}
            onDirtyChange={onDirtyChange}
            onComplete={navigateAfterSave}
            onCancel={activeMode === 'create' ? requestExit : cancelEdit}
            onCommit={onCommit}
          />
        )}
      </EntityWizardShell>
    </div>
  )
}
